import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { FileText, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { useMemo } from 'react';

const Invoices = ({ db, user }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('invoices_itemsPerPage')) || 10;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(sessionStorage.getItem('invoices_currentPage')) || 1;
  });
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('invoices_filters');
    return savedFilters ? JSON.parse(savedFilters) : {
      startDate: '',
      endDate: '',
      salesPerson: '',
      invoiceType: '', // Assuming 'invoiceType' based on context, might need adjustment
      status: '',
      customerName: '',
    };
  });

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('invoices_filters', JSON.stringify(filters));
    sessionStorage.setItem('invoices_currentPage', currentPage.toString());
    sessionStorage.setItem('invoices_itemsPerPage', itemsPerPage.toString());
  }, [filters, currentPage, itemsPerPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  // Filter and Sorting logic
  const filteredAndSortedInvoices = useMemo(() => {
    let filterableInvoices = [...invoices];

    if (!isAdmin) {
      filterableInvoices = filterableInvoices.filter(invoice => invoice.ownerUid && invoice.ownerUid === user?.uid);
    }

    // Apply filters
    filterableInvoices = filterableInvoices.filter(invoice => {
      // Date range filter
      if (filters.startDate || filters.endDate) {
        const invoiceDate = new Date(invoice.date || invoice.createdAt);
        invoiceDate.setHours(0, 0, 0, 0);

        if (filters.startDate) {
          const start = new Date(filters.startDate);
          start.setHours(0, 0, 0, 0);
          if (invoiceDate < start) return false;
        }

        if (filters.endDate) {
          const end = new Date(filters.endDate);
          end.setHours(0, 0, 0, 0);
          if (invoiceDate > end) return false;
        }
      }

      // Salesperson filter (case-insensitive substring match)
      if (filters.salesPerson && invoice.salesPerson?.toLowerCase().indexOf(filters.salesPerson.toLowerCase()) === -1) return false;
      // Invoice Type filter (case-insensitive exact match)
      if (filters.invoiceType && invoice.invoiceType?.toLowerCase() !== filters.invoiceType.toLowerCase()) return false;
      // Status filter (case-insensitive exact match)
      if (filters.status && invoice.status?.toLowerCase() !== filters.status.toLowerCase()) return false;
      // Customer Name filter (case-insensitive substring match)
      if (filters.customerName && invoice.customerName?.toLowerCase().indexOf(filters.customerName.toLowerCase()) === -1) return false;
      return true;
    });

    // Apply sorting
    if (sortColumn) {
      filterableInvoices.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (sortColumn === 'createdAt') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            if (sortDirection === 'asc') return dateA - dateB;
            return dateB - dateA;
        } else if (sortColumn === 'dueDate') {
            const dateA = new Date(aValue || '9999-12-31');
            const dateB = new Date(bValue || '9999-12-31');
            if (sortDirection === 'asc') return dateA - dateB;
            return dateB - dateA;
        } else if (sortColumn === 'grandTotal') {
            // Handle cases where grandTotal might be missing or null
            if (sortDirection === 'asc') return (aValue || 0) - (bValue || 0);
            return (bValue || 0) - (aValue || 0);
        } else if (typeof aValue === 'string') {
            if (sortDirection === 'asc') return aValue.localeCompare(bValue);
            return bValue.localeCompare(aValue);
        } else {
            // For other types like numbers or boolean (though not expected here)
            if (sortDirection === 'asc') return aValue - bValue;
            return bValue - aValue;
        }
      });
    }
    return filterableInvoices;
  }, [invoices, sortColumn, sortDirection, filters, isAdmin, user?.uid]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedInvoices.length / itemsPerPage);
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedInvoices.slice(startIndex, endIndex);
  }, [filteredAndSortedInvoices, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const invoicesRef = collection(db, "invoices");
        const invoicesQuery = !isAdmin && user?.uid
          ? query(invoicesRef, where('ownerUid', '==', user.uid))
          : invoicesRef;

        const querySnapshot = await getDocs(invoicesQuery);
        const invoicesList = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        // Sort by creation date (newest first)
        // invoicesList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setInvoices(invoicesList);
      } catch (error) {
        console.error("Error fetching invoices: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [db, isAdmin, user?.uid]);

  const renderStatus = (status, dueDate) => {
    switch (status) {
      case 'draft':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Draft</span>;
      case 'paid':
        return <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Paid</span>;
      case 'sent': {
        if (!dueDate) {
          return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Sent</span>;
        }
        const today = new Date();
        const due = new Date(dueDate);
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        const diffTime = due - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
          return <span style={{ color: '#0ea5e9', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Due in {diffDays} day{diffDays > 1 ? 's' : ''}</span>;
        } else if (diffDays < 0) {
          return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Overdue by {-diffDays} day{-diffDays > 1 ? 's' : ''}</span>;
        } else {
          return <span style={{ color: '#f97316', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Due Today</span>;
        }
      }
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
      if (!dateString) return '--';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px' }}>Invoice List ({filteredAndSortedInvoices.length})</h2>
      
      {/* Filter Section */}
      <div style={{ ...filterSectionStyle, gap: isMobile ? '10px' : '15px', padding: isMobile ? '12px' : '15px' }}>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Salesperson</label>
          <input type="text" name="salesPerson" value={filters.salesPerson} onChange={handleFilterChange} placeholder="Search Salesperson" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Invoice Type</label>
          <select name="invoiceType" value={filters.invoiceType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
          </select>
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      {invoices.length === 0 ? (
        <p style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          No invoices found. Click 'New Invoice' to create your first one.
        </p>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle} onClick={() => handleSort('createdAt')}>Date{getSortIcon('createdAt')}</th>
                <th style={{...thStyle, textAlign:'left'}} onClick={() => handleSort('customerName')}>Customer Name{getSortIcon('customerName')}</th>
                <th style={thStyle} onClick={() => handleSort('voucherNo')}>Invoice No.{getSortIcon('voucherNo')}</th>
                <th style={thStyle} onClick={() => handleSort('dueDate')}>Due Date{getSortIcon('dueDate')}</th>
                <th style={thStyle} onClick={() => handleSort('salesPerson')}>Salesperson{getSortIcon('salesPerson')}</th>
                <th style={thStyle} onClick={() => handleSort('grandTotal')}>Amount{getSortIcon('grandTotal')}</th>
                <th style={thStyle} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice) => (
                <tr 
                  key={invoice.id} 
                  style={{ borderBottom: '1px solid #e5e7eb' }}
                  className="clickable-row"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                >
                  <td style={tdStyle}>{formatDate(invoice.date || invoice.createdAt)}</td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{invoice.customerName}</div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>{invoice.subject?.substring(0, 40) || 'No Subject'}...</div>
                  </td>
                  <td style={tdStyle}>
                    <strong style={{fontSize: '13px'}}>{invoice.voucherNo || invoice.id.substring(0, 8).toUpperCase()}</strong>
                  </td>
                  <td style={tdStyle}>{formatDate(invoice.dueDate)}</td>
                  <td style={tdStyle}>{invoice.salesPerson || 'N/A'}</td>
                  <td style={{...tdStyle, fontWeight: 'bold'}}>
                    ₹{invoice.grandTotal ? invoice.grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '--'}
                  </td>
                  <td style={tdStyle}>
                    {renderStatus(invoice.status, invoice.dueDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            padding: '15px', 
            borderTop: '1px solid #e5e7eb',
            gap: isMobile ? '15px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: '13px', color: '#374151' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '5px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={paginationButtonStyle}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={paginationButtonStyle}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '12px 15px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', cursor: 'pointer' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', textAlign: 'center' };
const linkStyle = { color: '#1f2937', textDecoration: 'none', display: 'block' };
const paginationButtonStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '13px' };

const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

const viewButtonStyle = { 
  display: 'inline-flex', alignItems: 'center', gap: '5px', 
  padding: '6px 12px', borderRadius: '4px', 
  backgroundColor: '#e0f2f1', color: '#047857', 
  textDecoration: 'none', fontSize: '12px' 
};

export default Invoices;
